import { API_BASE_URL } from '@/config/api'
import type {
  BillingCustomer,
  BillingProductPage,
  BillingProduct,
  Challan,
  Invoice,
  Payment,
  ChallanItemInput,
  InvoiceItemInput,
  GstType,
  FinancialYear,
  FinancialYearSummary,
  DeliveryProof,
  Quote,
  QuoteCopy,
  QuoteTemplate,
  PaymentLink,
  PaymentLinksListResponse,
  VendorMaster,
  ShopSettings,
} from '@/types/billing'
import type { TransactionFilters, TransactionsListResponse } from '@/types/transaction'

export class BillingApiError extends Error {
  status: number
  data?: unknown

  constructor(message: string, status: number, data?: unknown) {
    super(message)
    this.name = 'BillingApiError'
    this.status = status
    this.data = data
  }
}

function extractBillingErrorMessage(text: string, status: number): { message: string; data?: unknown } {
  if (!text) return { message: `Request failed: ${status}` }

  try {
    const parsed = JSON.parse(text) as { error?: string; message?: string; data?: unknown }
    if (typeof parsed.error === 'string' && parsed.error.trim()) return { message: parsed.error, data: parsed.data }
    if (typeof parsed.message === 'string' && parsed.message.trim()) return { message: parsed.message, data: parsed.data }
  } catch {
    // Fall back to the raw response text when it is not JSON.
  }

  return { message: text }
}

export async function billingFetch<T = unknown>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  })
  if (!res.ok) {
    const text = await res.text().catch(() => 'Unknown error')
    const { message, data } = extractBillingErrorMessage(text, res.status)
    throw new BillingApiError(message, res.status, data)
  }
  return res.json()
}

async function billingFileFetch(path: string, options?: RequestInit): Promise<Blob> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    credentials: 'include',
  })
  if (!res.ok) {
    const text = await res.text().catch(() => 'Unknown error')
    const { message, data } = extractBillingErrorMessage(text, res.status)
    throw new BillingApiError(message, res.status, data)
  }
  return res.blob()
}

export interface ApiResponse<T> {
  success: boolean
  data: T
  error?: string
}

export interface BackupJob {
  id: string
  status: 'running' | 'completed' | 'failed'
  recipient: string
  start_date: string | null
  end_date: string | null
  result: Record<string, unknown> | null
  error: string | null
  created_at: string
  completed_at: string | null
}

export class BillingService {
  // Returns true if a fully-formed invoice number (e.g. "SM/26-27/0008")
  // already exists. Used to warn on blur before submit.
  static async checkInvoiceSerialExists(invoiceNumber: string): Promise<boolean> {
    const res = await billingFetch<{ success: boolean; data: { exists: boolean } }>(
      `/api/billing/invoices/serial-exists?number=${encodeURIComponent(invoiceNumber)}`,
    )
    return res.data.exists
  }

  // Returns true if a fully-formed challan number already exists.
  static async checkChallanSerialExists(challanNumber: string): Promise<boolean> {
    const res = await billingFetch<{ success: boolean; data: { exists: boolean } }>(
      `/api/billing/challans/serial-exists?number=${encodeURIComponent(challanNumber)}`,
    )
    return res.data.exists
  }

  // Reads the actual server-side counter (Postgres sequence for invoices,
  // MAX(challan_number)+1 for challans) without consuming a number — unlike
  // guessing "last fetched invoice + 1" client-side, this can't drift from
  // what next_invoice_number()/next_challan_number() will really return.
  static async getNextInvoiceNumber(): Promise<string> {
    const res = await billingFetch<{ success: boolean; data: { next_number: string } }>(
      '/api/billing/invoices/next-number',
    )
    return res.data.next_number
  }

  static async getNextChallanNumber(): Promise<string> {
    const res = await billingFetch<{ success: boolean; data: { next_number: string } }>(
      '/api/billing/challans/next-number',
    )
    return res.data.next_number
  }

  // Auth
  // ── WebAuthn / Passkey ──
  static webauthnRegisterOptions(email: string) {
    return billingFetch<import('@simplewebauthn/browser').PublicKeyCredentialCreationOptionsJSON>('/api/billing/auth/webauthn/register-options', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  }

  static webauthnRegister(email: string, response: unknown, deviceName?: string) {
    return billingFetch<{ verified: boolean }>('/api/billing/auth/webauthn/register', {
      method: 'POST',
      body: JSON.stringify({ email, response, deviceName }),
    })
  }

  static webauthnLoginOptions(email: string) {
    return billingFetch<import('@simplewebauthn/browser').PublicKeyCredentialRequestOptionsJSON>('/api/billing/auth/webauthn/login-options', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  }

  static webauthnRemove(email: string) {
    return billingFetch<{ success: boolean }>('/api/billing/auth/webauthn/credentials', {
      method: 'DELETE',
      body: JSON.stringify({ email }),
    })
  }

  static webauthnLogin(email: string, response: unknown) {
    return billingFetch<{ success: boolean; user?: { name: string; email: string; role: string } }>('/api/billing/auth/webauthn/login', {
      method: 'POST',
      body: JSON.stringify({ email, response }),
    })
  }

  static login(data: { email: string; password: string } | string) {
    const body = typeof data === 'string' ? { password: data } : data
    return billingFetch<{ success: boolean; user?: { name: string; email: string; role: string } }>('/api/billing/auth/login', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  static requestLoginOtp(email: string) {
    return billingFetch<{ success: boolean; message: string }>('/api/billing/auth/login-otp/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  }

  static loginWithOtp(email: string, otp: string) {
    return billingFetch<{ success: boolean; user?: { name: string; email: string; role: string } }>('/api/billing/auth/login-otp/verify', {
      method: 'POST',
      body: JSON.stringify({ email, otp }),
    })
  }

  static signup(data: { token: string; password: string; name: string }) {
    return billingFetch<{ success: boolean; user: { name: string; email: string; role: string } }>('/api/billing/auth/signup', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  static forgotPassword(email: string) {
    return billingFetch<{ success: boolean; message: string }>('/api/billing/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  }

  static resetPassword(email: string, otp: string, newPassword: string) {
    return billingFetch<{ success: boolean; message: string }>('/api/billing/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ email, otp, newPassword }),
    })
  }

  static logout() {
    return billingFetch<{ success: boolean }>('/api/billing/auth/logout', { method: 'POST' })
  }

  static me() {
    return billingFetch<{ success: boolean; role: string }>('/api/billing/auth/me')
  }

  // Customers
  static listCustomers(search?: string) {
    const q = search ? `?search=${encodeURIComponent(search)}` : ''
    return billingFetch<ApiResponse<BillingCustomer[]>>(`/api/billing/customers${q}`)
  }

  static getCustomer(id: string) {
    return billingFetch<ApiResponse<BillingCustomer & { open_challans_count: number }>>(
      `/api/billing/customers/${id}`
    )
  }

  static createCustomer(data: Partial<BillingCustomer>) {
    return billingFetch<ApiResponse<BillingCustomer>>('/api/billing/customers', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  static updateCustomer(id: string, data: Partial<BillingCustomer>) {
    return billingFetch<ApiResponse<BillingCustomer>>(`/api/billing/customers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  // Products
  static listProducts(search?: string) {
    const q = search ? `?search=${encodeURIComponent(search)}` : ''
    return billingFetch<ApiResponse<BillingProduct[]>>(`/api/billing/products${q}`)
  }

  static listProductsPage(search?: string, page: number = 1, limit: number = 25, isActive?: boolean, vendors?: string[], hasImage?: boolean, stockFilter?: 'out_of_stock' | 'low_stock' | 'in_stock') {
    const params = new URLSearchParams()
    if (search) params.set('search', search)
    params.set('page', String(page))
    params.set('limit', String(limit))
    if (isActive !== undefined) params.set('is_active', String(isActive))
    if (vendors?.length) params.set('vendors', vendors.join(','))
    if (hasImage !== undefined) params.set('has_image', String(hasImage))
    if (stockFilter) params.set('stock_filter', stockFilter)
    return billingFetch<ApiResponse<BillingProductPage>>(`/api/billing/products/paginated?${params.toString()}`)
  }

  static listProductVendors() {
    return billingFetch<ApiResponse<string[]>>('/api/billing/products/vendors')
  }

  static listVendors() {
    return billingFetch<ApiResponse<VendorMaster[]>>('/api/billing/vendors')
  }

  static createVendor(name: string) {
    return billingFetch<ApiResponse<VendorMaster>>('/api/billing/vendors', {
      method: 'POST',
      body: JSON.stringify({ name }),
    })
  }

  static getProductByBarcode(barcode: string) {
    return billingFetch<ApiResponse<BillingProduct>>(
      `/api/billing/products/barcode/${encodeURIComponent(barcode)}`
    )
  }

  static suggestProductFields(productName: string, existingCategories?: string[], clarification?: string) {
    return billingFetch<{
      success: boolean
      data:
        | { description: string; hsn_code: string; gst_rate: number; category: string; product_type: string; tags: string[] }
        | { needs_clarification: true; question: string }
    }>('/api/billing/products/ai-suggest', {
      method: 'POST',
      body: JSON.stringify({ productName, existingCategories, clarification }),
    })
  }

  static createProduct(data: Partial<BillingProduct>) {
    return billingFetch<ApiResponse<BillingProduct>>('/api/billing/products', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  static updateProduct(id: string, data: Partial<BillingProduct>) {
    return billingFetch<ApiResponse<BillingProduct>>(`/api/billing/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  static uploadProductImage(id: string, imageBase64: string) {
    return billingFetch<ApiResponse<BillingProduct>>(`/api/billing/products/${id}/image`, {
      method: 'POST',
      body: JSON.stringify({ image: imageBase64 }),
    })
  }

  static uploadGalleryImage(productId: string, imageBase64: string) {
    return billingFetch<ApiResponse<any>>(`/api/billing/products/${productId}/gallery-image`, {
      method: 'POST',
      body: JSON.stringify({ image: imageBase64 }),
    })
  }

  static getProductDetails(id: string) {
    return billingFetch<ApiResponse<any>>(`/api/billing/products/${id}/details`)
  }

  // Variants
  static createVariant(productId: string, data: any) {
    return billingFetch<ApiResponse<any>>(`/api/billing/products/${productId}/variants`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  static updateVariant(id: string, data: any) {
    return billingFetch<ApiResponse<any>>(`/api/billing/variants/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  static deleteVariant(id: string) {
    return billingFetch<ApiResponse<void>>(`/api/billing/variants/${id}`, {
      method: 'DELETE',
    })
  }

  static deleteProduct(id: string) {
    return billingFetch<ApiResponse<void>>(`/api/billing/products/${id}`, {
      method: 'DELETE',
    })
  }

  static exportProductsToEmail() {
    return billingFetch<ApiResponse<{ message: string }>>('/api/billing/products/export-email', {
      method: 'POST',
    })
  }

  static async downloadSampleProductCsv() {
    const blob = await billingFileFetch('/api/billing/products/sample-csv')
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'products-sample.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  static importProductsCsv(csvContent: string) {
    return billingFetch<ApiResponse<{ message: string; totalRows: number }>>('/api/billing/products/import-csv', {
      method: 'POST',
      headers: { 'Content-Type': 'text/csv' },
      body: csvContent,
    })
  }

  // Product Images
  static addProductImage(productId: string, url: string, altText?: string) {
    return billingFetch<ApiResponse<any>>(`/api/billing/products/${productId}/images`, {
      method: 'POST',
      body: JSON.stringify({ url, alt_text: altText }),
    })
  }

  static deleteProductImage(imageId: string) {
    return billingFetch<ApiResponse<void>>(`/api/billing/images/${imageId}`, {
      method: 'DELETE',
    })
  }

  // Challans
  static listChallans(filters?: {
    customer_id?: string
    status?: string
    start_date?: string
    end_date?: string
  }) {
    const params = new URLSearchParams()
    if (filters?.customer_id) params.set('customer_id', filters.customer_id)
    if (filters?.status) params.set('status', filters.status)
    if (filters?.start_date) params.set('start_date', filters.start_date)
    if (filters?.end_date) params.set('end_date', filters.end_date)
    const q = params.toString() ? `?${params.toString()}` : ''
    return billingFetch<ApiResponse<Challan[]>>(`/api/billing/challans${q}`)
  }

  static getChallan(id: string) {
    return billingFetch<ApiResponse<Challan>>(`/api/billing/challans/${id}`)
  }

  static createChallan(data: {
    customer_id: string
    receiver_name?: string
    delivery_date?: string
    notes?: string
    show_rates?: boolean
    shop_phone?: string
    shop_email?: string
    custom_serial?: string
    items: ChallanItemInput[]
  }) {
    return billingFetch<ApiResponse<Challan>>('/api/billing/challans', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  static updateChallan(id: string, data: {
    customer_id?: string
    receiver_name?: string
    delivery_date?: string
    notes?: string
    show_rates?: boolean
    shop_phone?: string
    shop_email?: string
    items: ChallanItemInput[]
  }) {
    return billingFetch<ApiResponse<Challan>>(`/api/billing/challans/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  static cancelChallan(id: string) {
    return billingFetch<ApiResponse<Challan>>(`/api/billing/challans/${id}/cancel`, { method: 'POST' })
  }

  static getChallanPdfUrl(id: string, opts: { withDuplicate?: boolean } = {}) {
    const q = opts.withDuplicate ? '?duplicate=1' : ''
    return `${API_BASE_URL}/api/billing/challans/${id}/pdf${q}`
  }

  static uploadDeliveryProof(challanId: string, imageBase64: string) {
    return billingFetch<ApiResponse<Challan>>(`/api/billing/challans/${challanId}/delivery-proof`, {
      method: 'POST',
      body: JSON.stringify({ image: imageBase64 }),
    })
  }

  static getInvoiceDeliveryProofs(invoiceId: string) {
    return billingFetch<ApiResponse<DeliveryProof[]>>(`/api/billing/invoices/${invoiceId}/delivery-proofs`)
  }

  // Invoices
  static listInvoices(filters?: {
    customer_id?: string
    start_date?: string
    end_date?: string
  }) {
    const params = new URLSearchParams()
    if (filters?.customer_id) params.set('customer_id', filters.customer_id)
    if (filters?.start_date) params.set('start_date', filters.start_date)
    if (filters?.end_date) params.set('end_date', filters.end_date)
    const q = params.toString() ? `?${params.toString()}` : ''
    return billingFetch<ApiResponse<Invoice[]>>(`/api/billing/invoices${q}`)
  }

  static getInvoice(id: string) {
    return billingFetch<ApiResponse<Invoice>>(`/api/billing/invoices/${id}`)
  }

  static createRetailInvoice(data: {
    customer_id?: string
    channel?: string
    payment_mode?: string
    gst_type?: GstType
    supply_type?: 'domestic' | 'sez_lut'
    po_number?: string
    notes?: string
    shop_phone?: string
    shop_email?: string
    discount_type?: 'flat' | 'percentage'
    discount_value?: number
    invoice_date?: string
    custom_serial?: string
    extra_charges?: Array<{ label: string; amount: number; type: 'add' | 'sub' }>
    items: InvoiceItemInput[]
  }) {
    return billingFetch<ApiResponse<Invoice>>('/api/billing/invoices', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  static updateRetailInvoice(id: string, data: {
    customer_id?: string
    payment_mode?: string
    gst_type?: GstType
    po_number?: string
    notes?: string
    shop_phone?: string
    shop_email?: string
    discount_type?: 'flat' | 'percentage'
    discount_value?: number
    invoice_date?: string
    extra_charges?: Array<{ label: string; amount: number; type: 'add' | 'sub' }>
    items: InvoiceItemInput[]
  }) {
    return billingFetch<ApiResponse<Invoice>>(`/api/billing/invoices/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  static cancelInvoice(id: string) {
    return billingFetch<ApiResponse<Invoice>>(`/api/billing/invoices/${id}/cancel`, { method: 'POST' })
  }

  static deleteInvoice(id: string, reason: string, deletedBy: string) {
    return billingFetch<ApiResponse<{ id: string }>>(`/api/billing/invoices/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ reason, deleted_by: deletedBy }),
    })
  }

  static convertQuoteToInvoice(quoteId: string, data: {
    customer_id?: string
    payment_mode?: string
  }) {
    return billingFetch<ApiResponse<Invoice>>(`/api/billing/quotes/${quoteId}/to-invoice`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  static convertChallansToInvoice(data: {
    challan_ids: string[]
    billing_period_start?: string
    billing_period_end?: string
    payment_mode?: string
    gst_type?: GstType
    notes?: string
  }) {
    return billingFetch<ApiResponse<Invoice>>('/api/billing/invoices/from-challans', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  static getInvoicePdfUrl(id: string, opts: { withDuplicate?: boolean } = {}) {
    const q = opts.withDuplicate ? '?duplicate=1' : ''
    return `${API_BASE_URL}/api/billing/invoices/${id}/pdf${q}`
  }

  static getInvoiceThermalPdfUrl(id: string) {
    return `${API_BASE_URL}/api/billing/invoices/${id}/thermal-pdf`
  }

  static sendInvoiceWhatsapp(id: string) {
    return billingFetch<{ success: boolean }>(`/api/billing/invoices/${id}/whatsapp`, { method: 'POST' })
  }

  static sendInvoiceEmail(id: string, email: string, opts: { withDuplicate?: boolean } = {}) {
    return billingFetch<{ success: boolean }>(`/api/billing/invoices/${id}/email`, {
      method: 'POST',
      body: JSON.stringify({ email, duplicate: !!opts.withDuplicate }),
    })
  }

  static sendChallanEmail(id: string, email: string, opts: { withDuplicate?: boolean } = {}) {
    return billingFetch<{ success: boolean }>(`/api/billing/challans/${id}/email`, {
      method: 'POST',
      body: JSON.stringify({ email, duplicate: !!opts.withDuplicate }),
    })
  }

  // Paginated Invoices
  static listInvoicesPage(filters?: {
    search?: string; customer_id?: string; channel?: string;
    start_date?: string; end_date?: string;
  }, page: number = 1, limit: number = 25) {
    const params = new URLSearchParams()
    if (filters?.search) params.set('search', filters.search)
    if (filters?.customer_id) params.set('customer_id', filters.customer_id)
    if (filters?.channel) params.set('channel', filters.channel)
    if (filters?.start_date) params.set('start_date', filters.start_date)
    if (filters?.end_date) params.set('end_date', filters.end_date)
    params.set('page', String(page))
    params.set('limit', String(limit))
    return billingFetch<ApiResponse<{ items: Invoice[]; page: number; pageSize: number; total: number; hasMore: boolean }>>(`/api/billing/invoices/paginated?${params.toString()}`)
  }

  // Paginated Challans
  static listChallansPage(filters?: {
    search?: string; customer_id?: string; status?: string;
    start_date?: string; end_date?: string;
  }, page: number = 1, limit: number = 25) {
    const params = new URLSearchParams()
    if (filters?.search) params.set('search', filters.search)
    if (filters?.customer_id) params.set('customer_id', filters.customer_id)
    if (filters?.status) params.set('status', filters.status)
    if (filters?.start_date) params.set('start_date', filters.start_date)
    if (filters?.end_date) params.set('end_date', filters.end_date)
    params.set('page', String(page))
    params.set('limit', String(limit))
    return billingFetch<ApiResponse<{ items: Challan[]; page: number; pageSize: number; total: number; hasMore: boolean }>>(`/api/billing/challans/paginated?${params.toString()}`)
  }

  // Payments
  static recordPayment(data: { invoice_id: string; amount: number; mode: string; reference_no?: string }) {
    return billingFetch<ApiResponse<Payment>>('/api/billing/payments', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  // Inventory
  static listInventoryPage(search?: string, page: number = 1, limit: number = 25) {
    const params = new URLSearchParams()
    if (search) params.set('search', search)
    params.set('page', String(page))
    params.set('limit', String(limit))
    return billingFetch<ApiResponse<BillingProductPage>>(`/api/billing/inventory/paginated?${params.toString()}`)
  }

  static listInventory(search?: string) {
    const q = search ? `?search=${encodeURIComponent(search)}` : ''
    return billingFetch<ApiResponse<BillingProduct[]>>(`/api/billing/inventory${q}`)
  }

  static adjustStock(data: { product_id: string; qty_change: number; reason?: string }) {
    return billingFetch<ApiResponse<BillingProduct>>('/api/billing/inventory/adjust', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  // GST Export
  static gstExportUrl(start: string, end: string) {
    return `${API_BASE_URL}/api/billing/gst-export?start=${start}&end=${end}`
  }

  // GSTR-1 JSON Export
  static gstr1JsonExportUrl(start: string, end: string) {
    return `${API_BASE_URL}/api/billing/gstr1-json?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`
  }

  static emailGstr1Json(email: string, start: string, end: string) {
    return billingFetch<{ success: boolean; message: string }>('/api/billing/gstr1-json/email', {
      method: 'POST',
      body: JSON.stringify({ email, start, end }),
    })
  }

  // Backup
  static getBackupDownloadUrl(start_date?: string, end_date?: string) {
    const params = new URLSearchParams()
    if (start_date) params.set('start_date', start_date)
    if (end_date) params.set('end_date', end_date)
    const q = params.toString() ? `?${params.toString()}` : ''
    return `${API_BASE_URL}/api/billing/backup/download${q}`
  }

  static emailBackupZip(email: string, start_date?: string, end_date?: string) {
    return billingFetch<ApiResponse<BackupJob> & { message: string }>('/api/billing/backup/email', {
      method: 'POST',
      body: JSON.stringify({ email, start_date, end_date }),
    })
  }

  static getBackupStatus() {
    return billingFetch<ApiResponse<BackupJob | null>>('/api/billing/backup/status')
  }

  static getBackupSettings() {
    return billingFetch<ApiResponse<{ weeklyRecipientEmail: string | null }>>('/api/billing/backup/settings')
  }

  static updateBackupSettings(weeklyRecipientEmail: string | null) {
    return billingFetch<ApiResponse<{ weeklyRecipientEmail: string | null }>>('/api/billing/backup/settings', {
      method: 'PUT',
      body: JSON.stringify({ weeklyRecipientEmail }),
    })
  }

  // Shop Settings (identity, bank, terms, LUT ARN — see admin Settings page)
  static getShopSettings() {
    return billingFetch<ApiResponse<ShopSettings>>('/api/billing/settings')
  }

  static updateShopSettings(data: Partial<Omit<ShopSettings, 'updated_at'>>) {
    return billingFetch<ApiResponse<ShopSettings>>('/api/billing/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  // Store Settings
  static getStoreSignature() {
    return billingFetch<ApiResponse<{ signature_url: string | null }>>('/api/billing/settings/signature')
  }

  static uploadStoreSignature(imageBase64: string) {
    return billingFetch<ApiResponse<{ signature_url: string }>>('/api/billing/settings/signature', {
      method: 'POST',
      body: JSON.stringify({ image: imageBase64 }),
    })
  }

  static deleteStoreSignature() {
    return billingFetch<ApiResponse<null>>('/api/billing/settings/signature', {
      method: 'DELETE',
    })
  }

  // Financial Years
  static getSerialGaps(type: 'invoice' | 'challan', fyId: string) {
    return billingFetch<ApiResponse<string[]>>(`/api/billing/serial-gaps?type=${type}&fy_id=${encodeURIComponent(fyId)}`)
  }

  static getFinancialYears() {
    return billingFetch<ApiResponse<FinancialYear[]>>('/api/billing/financial-years')
  }

  static getCurrentFY() {
    return billingFetch<ApiResponse<FinancialYear>>('/api/billing/financial-years/current')
  }

  static closeFY(id: string) {
    return billingFetch<ApiResponse<FinancialYear>>(`/api/billing/financial-years/${id}/close`, { method: 'POST' })
  }

  static reopenFY(id: string) {
    return billingFetch<ApiResponse<FinancialYear>>(`/api/billing/financial-years/${id}/reopen`, { method: 'POST' })
  }

  static getFYSummary(id: string) {
    return billingFetch<ApiResponse<FinancialYearSummary>>(`/api/billing/financial-years/${id}/summary`)
  }

  // Letterhead
  static getLetterheadPdfUrl(orgName?: string) {
    const params = orgName ? `?org_name=${encodeURIComponent(orgName)}` : ''
    return `${API_BASE_URL}/api/billing/letterhead/pdf${params}`
  }

  // Quotes
  static listQuotes(search?: string) {
    const q = search ? `?search=${encodeURIComponent(search)}` : ''
    return billingFetch<ApiResponse<Quote[]>>(`/api/billing/quotes${q}`)
  }

  static getQuote(id: string) {
    return billingFetch<ApiResponse<Quote>>(`/api/billing/quotes/${id}`)
  }

  static createQuote(data: {
    org_name?: string
    customer_name?: string
    customer_email?: string
    customer_phone?: string
    customer_gstin?: string
    customer_address?: string
    gst_type?: GstType
    template?: QuoteTemplate
    valid_until?: string
    notes?: string
    terms_conditions?: string
    bank_details?: string
    show_tax?: boolean
    show_total?: boolean
    shop_phone?: string
    shop_email?: string
    shop_address?: string
    shop_gstin?: string
    items: Array<{
      product_id?: string
      description: string
      brand?: string
      hsn_code?: string
      qty: number
      unit?: string
      unit_price: number
      gst_rate: number
    }>
  }) {
    return billingFetch<ApiResponse<Quote>>('/api/billing/quotes', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  static updateQuote(id: string, data: Parameters<typeof BillingService.createQuote>[0]) {
    return billingFetch<ApiResponse<Quote>>(`/api/billing/quotes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  static getQuotePdfUrl(id: string, template?: import('@/types/billing').QuoteTemplate, show_tax?: boolean, show_total?: boolean, show_rate?: boolean) {
    const params = new URLSearchParams()
    if (template) params.set('template', template)
    if (show_tax !== undefined) params.set('show_tax', String(show_tax))
    if (show_total !== undefined) params.set('show_total', String(show_total))
    if (show_rate !== undefined) params.set('show_rate', String(show_rate))
    const qs = params.toString()
    return `${API_BASE_URL}/api/billing/quotes/${id}/pdf${qs ? `?${qs}` : ''}`
  }

  static sendQuoteEmail(id: string, email: string) {
    return billingFetch<{ success: boolean }>(`/api/billing/quotes/${id}/email`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  }

  // Quote Copies
  static createQuoteCopy(quoteId: string, data: Parameters<typeof BillingService.createQuote>[0] & { custom_serial?: string }) {
    return billingFetch<ApiResponse<QuoteCopy>>(`/api/billing/quotes/${quoteId}/copies`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  static listQuoteCopies(quoteId: string) {
    return billingFetch<ApiResponse<QuoteCopy[]>>(`/api/billing/quotes/${quoteId}/copies`)
  }

  static getQuoteCopy(copyId: string) {
    return billingFetch<ApiResponse<QuoteCopy>>(`/api/billing/quote-copies/${copyId}`)
  }

  static updateQuoteCopy(copyId: string, data: Parameters<typeof BillingService.createQuote>[0] & { custom_serial?: string }) {
    return billingFetch<ApiResponse<QuoteCopy>>(`/api/billing/quote-copies/${copyId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  static deleteQuoteCopy(copyId: string) {
    return billingFetch<ApiResponse<void>>(`/api/billing/quote-copies/${copyId}`, {
      method: 'DELETE',
    })
  }

  static getQuoteCopyPdfUrl(copyId: string, template?: QuoteTemplate, show_tax?: boolean, show_total?: boolean, show_rate?: boolean) {
    const params = new URLSearchParams()
    if (template) params.set('template', template)
    if (show_tax !== undefined) params.set('show_tax', String(show_tax))
    if (show_total !== undefined) params.set('show_total', String(show_total))
    if (show_rate !== undefined) params.set('show_rate', String(show_rate))
    const qs = params.toString()
    return `${API_BASE_URL}/api/billing/quote-copies/${copyId}/pdf${qs ? `?${qs}` : ''}`
  }

  // ─── Payment Links ───

  static listPaymentLinks(search?: string, limit = 50, offset = 0) {
    const params = new URLSearchParams()
    if (search) params.set('search', search)
    params.set('limit', String(limit))
    params.set('offset', String(offset))
    return billingFetch<PaymentLinksListResponse>(`/api/billing/payment-links?${params}`)
  }

  static createPaymentLink(data: {
    amount: number
    description?: string
    customer_name?: string
    customer_email?: string
    customer_phone?: string
    expire_by_minutes?: number
  }) {
    return billingFetch<ApiResponse<PaymentLink>>('/api/billing/payment-links', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  static createInvoicePaymentLink(invoiceId: string, data?: {
    customer_email?: string
    customer_phone?: string
    customer_name?: string
    expire_by_minutes?: number
  }) {
    return billingFetch<ApiResponse<PaymentLink>>(`/api/billing/invoices/${invoiceId}/payment-link`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    })
  }

  static resendPaymentLink(linkId: string, email?: string) {
    return billingFetch<ApiResponse<{ success: true }>>(`/api/billing/payment-links/${linkId}/resend`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  }

  static listTransactions(filters?: TransactionFilters) {
    const params = new URLSearchParams()
    if (filters?.search) params.set('search', filters.search)
    if (filters?.status) params.set('status', filters.status)
    if (filters?.method) params.set('method', filters.method)
    if (filters?.source) params.set('source', filters.source)
    if (filters?.start_date) params.set('start_date', filters.start_date)
    if (filters?.end_date) params.set('end_date', filters.end_date)
    if (filters?.page) params.set('page', String(filters.page))
    if (filters?.limit) params.set('limit', String(filters.limit))
    const query = params.toString()
    return billingFetch<TransactionsListResponse>(`/api/billing/transactions${query ? `?${query}` : ''}`)
  }
}
